import { useState } from 'react';
import { motion } from 'framer-motion';
import { Building2, CheckCircle2, Shield, Zap } from 'lucide-react';
import { LoginForm } from '@/components/Forms';
import { PublicLayout } from '@/components/PublicLayout';
import { useAuth } from '@/hooks/useAuth';
import { IMAGES } from '@/assets/images';
import { useToast } from '@/hooks/use-toast';

export default function Login() {
  const { signIn } = useAuth();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);

  const handleLogin = async (email: string, password: string) => {
    setIsLoading(true);
    try {
      const { error } = await signIn(email, password);
      if (error) {
        toast({
          title: 'Errore di accesso',
          description: error.message || 'Credenziali non valide',
          variant: 'destructive',
        });
      }
      // Il redirect a dashboard avviene automaticamente tramite RedirectIfAuthenticated in App.tsx
      // Non serve navigate() manuale
    } catch {
      toast({
        title: 'Errore',
        description: "Si è verificato un errore durante l'accesso",
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <PublicLayout>
      <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 bg-sidebar">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md space-y-8"
        >
          <div className="text-center">
            <div className="flex justify-center mb-6">
              <img
                src={IMAGES.LOGO_EGIM_25}
                alt="EGIM Logo"
                className="h-16 w-auto"
              />
            </div>
            <h2 className="text-3xl font-bold text-sidebar-foreground mb-2">
              EGIM SM
            </h2>
            <p className="text-sidebar-foreground/70">
              Accedi alla piattaforma di analisi consuntivi
            </p>
          </div>

          <div className="bg-card rounded-2xl shadow-lg border border-border p-8">
            <LoginForm onSubmit={handleLogin} />
          </div>

          <div className="bg-sidebar-accent rounded-xl p-6 space-y-4">
            <h3 className="text-sm font-semibold text-sidebar-foreground flex items-center gap-2">
              <Shield className="w-4 h-4 text-primary" />
              Perché EgimConsuntivi?
            </h3>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <Zap className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-sidebar-foreground">Analisi AI Automatica</p>
                  <p className="text-xs text-sidebar-foreground/70">Carica PDF e ottieni analisi complete in secondi</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-sidebar-foreground">Grafici Interattivi</p>
                  <p className="text-xs text-sidebar-foreground/70">Visualizza scostamenti e trend professionali</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Building2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-sidebar-foreground">Multi-Condominio</p>
                  <p className="text-xs text-sidebar-foreground/70">Gestisci tutti i tuoi condomini in un'unica piattaforma</p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </PublicLayout>
  );
}
